import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { authUtils } from '../utils/authUtils'
import { Navigate, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, RefreshCw } from 'lucide-react'

const LoginPage = () => {
  const { login, isAuthenticated, profile } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const navigate = useNavigate()

  // Redirecionar se já estiver autenticado
  if (isAuthenticated()) {
    const redirectTo = profile?.role === 'aluno' ? '/simulado' : '/dashboard'
    return <Navigate to={redirectTo} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      const { error, user } = await login({ email, password })

      if (error) {
        setError('Email ou senha incorretos. Tente novamente.')
        setIsSubmitting(false)
      } else if (user) {
        // Navegação imediata após login bem-sucedido
        const redirectTo = user.profile?.role === 'aluno' ? '/simulado' : '/dashboard'
        navigate(redirectTo, { replace: true })
        // Não definir isSubmitting = false aqui pois estamos navegando
      }
    } catch (err) {
      setError('Erro interno. Tente novamente mais tarde.')
      setIsSubmitting(false)
    }
  }

  const handleClearSession = async () => {
    setIsClearing(true)
    try {
      await authUtils.clearSession()
      setError('')
      // Recarregar a página para garantir estado limpo
      window.location.reload()
    } catch (err) {
      setError('Erro ao limpar sessão')
    } finally {
      setIsClearing(false)
    }
  }


  // Não mostrar loading se já temos informação de autenticação
  // O redirect acima já cuida de usuários autenticados

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-accent-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-48 w-48 flex items-center justify-center">
            <img
              src="https://kufrgfjlfhdinewzkwzg.supabase.co/storage/v1/object/public/site/logo.png"
              alt="Bizu Treinamentos Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-900">
            <span className="text-secondary-600">Bizu</span> Treinamentos
          </h2>
          <p className="mt-2 text-center text-sm text-accent-600">
            Sistema Administrativo
          </p>
          <div className="mt-4 text-center">
            <a
              href="https://bizutreinamentos.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              ← Voltar para o site
            </a>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Senha
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting || !email || !password}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Entrar'
              )}
            </button>
          </div>

          {/* Botão para limpar sessão em caso de erro */}
          <div className="mt-2">
            <button
              type="button"
              onClick={handleClearSession}
              disabled={isClearing}
              className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isClearing ? 'animate-spin' : ''}`} />
              {isClearing ? 'Limpando...' : 'Limpar Sessão'}
            </button>
            <p className="mt-1 text-xs text-gray-500 text-center">
              Use este botão se estiver com problemas de login
            </p>
          </div>


        </form>
      </div>
    </div>
  )
}

export default LoginPage